<template>
  <v-container class="auth-page py-6" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="10" md="8" lg="7" xl="6">
        <v-card class="auth-card" elevation="2">
          <!-- Header -->
          <div class="auth-header">
            <div class="auth-title-block">
              <div class="auth-title-icon">
                <v-icon color="primary" size="28">
                  mdi-account-plus-outline
                </v-icon>
              </div>

              <div>
                <div class="text-h6 font-weight-bold">
                  Registrieren
                </div>

                <div class="text-body-2 text-medium-emphasis">
                  Erstellen Sie ein neues Benutzerkonto.
                </div>
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="auth-card-content">
            <v-alert
              type="info"
              variant="tonal"
              border="start"
              class="registration-hint"
            >
              Nach der Registrierung muss Ihr Konto von einem Administrator
              freigeschaltet werden.
            </v-alert>

            <v-form
              ref="formRef"
              v-model="valid"
              class="auth-form"
              @submit.prevent="register"
            >
              <!-- Personal data -->
              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-account-outline
                  </v-icon>

                  <span>Persönliche Angaben</span>
                </div>

                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.trim="firstName"
                      :rules="[requiredRule]"
                      label="Vorname"
                      autocomplete="given-name"
                      prepend-inner-icon="mdi-account-outline"
                      :disabled="saving"
                      validate-on="blur"
                      class="mb-1"
                    />
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.trim="lastName"
                      :rules="[requiredRule]"
                      label="Nachname"
                      autocomplete="family-name"
                      prepend-inner-icon="mdi-account-outline"
                      :disabled="saving"
                      validate-on="blur"
                      class="mb-1"
                    />
                  </v-col>
                </v-row>

                <v-text-field
                  v-model.trim="email"
                  type="email"
                  :rules="emailRules"
                  :error-messages="fieldErrors.email"
                  label="E-Mail-Adresse"
                  autocomplete="email"
                  prepend-inner-icon="mdi-email-outline"
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-1"
                  @update:model-value="clearFieldError('email')"
                />
              </div>

              <!-- Password -->
              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-lock-plus-outline
                  </v-icon>

                  <span>Passwort festlegen</span>
                </div>

                <v-text-field
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  :rules="[requiredRule, minLengthRule]"
                  :error-messages="fieldErrors.password"
                  label="Passwort"
                  autocomplete="new-password"
                  prepend-inner-icon="mdi-lock-plus-outline"
                  :append-inner-icon="
                    showPassword ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-3"
                  @click:append-inner="showPassword = !showPassword"
                  @update:model-value="clearFieldError('password')"
                />

                <v-text-field
                  v-model="password2"
                  :type="showPassword2 ? 'text' : 'password'"
                  :rules="[requiredRule, minLengthRule, matchRule]"
                  label="Passwort wiederholen"
                  autocomplete="new-password"
                  prepend-inner-icon="mdi-lock-check-outline"
                  :append-inner-icon="
                    showPassword2 ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-1"
                  @click:append-inner="showPassword2 = !showPassword2"
                />
              </div>
            </v-form>
          </v-card-text>

          <v-divider />

          <!-- Footer -->
          <div class="auth-footer">
            <v-btn
              variant="text"
              :disabled="saving"
              @click="goToLogin"
            >
              Bereits registriert?
            </v-btn>

            <v-btn
              color="primary"
              variant="elevated"
              :loading="saving"
              :disabled="!valid || saving"
              class="submit-button"
              @click="register"
            >
              <v-icon start>mdi-account-plus-outline</v-icon>
              Registrieren
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success dialog -->
    <v-dialog
      v-model="showSuccessDialog"
      max-width="540"
      persistent
    >
      <v-card class="auth-dialog">
        <div class="dialog-header">
          <div class="dialog-icon success-icon">
            <v-icon color="success" size="26">
              mdi-account-check-outline
            </v-icon>
          </div>

          <div>
            <div class="text-h6 font-weight-bold">
              Registrierung erfolgreich
            </div>

            <div class="text-body-2 text-medium-emphasis">
              Ihr Benutzerkonto wurde erstellt
            </div>
          </div>
        </div>

        <v-divider />

        <v-card-text class="dialog-content">
          <p>
            Vielen Dank für Ihre Registrierung. Ihr Konto muss nun von
            einem Administrator freigeschaltet werden.
          </p>

          <p class="mt-3 mb-0">
            Um den Vorgang zu beschleunigen, können Sie eine E-Mail an
            <a
              href="mailto:earns@rom-technik.de"
              class="support-link"
            >
              earns@rom-technik.de
            </a>
            senden.
          </p>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog-actions">
          <v-spacer />

          <v-btn
            color="primary"
            variant="elevated"
            @click="goToLogin"
          >
            Zur Anmeldung
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/plugins/axios'
import { emailMatch } from '@/utils'

const router = useRouter()

const formRef = ref(null)
const valid = ref(false)
const saving = ref(false)

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const password2 = ref('')

const showPassword = ref(false)
const showPassword2 = ref(false)
const showSuccessDialog = ref(false)

const fieldErrors = ref({
  email: [],
  password: [],
})

const requiredRule = value =>
  !!value || 'Dieses Feld muss ausgefüllt sein.'

const minLengthRule = value =>
  !value ||
  value.length >= 8 ||
  'Das Passwort muss mindestens 8 Zeichen lang sein.'

const matchRule = value =>
  value === password.value ||
  'Die Passwörter stimmen nicht überein.'

const emailRules = [
  value =>
    (!!value && emailMatch(value)) ||
    'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
]

function clearFieldError(field) {
  fieldErrors.value[field] = []
}

function resetFieldErrors() {
  fieldErrors.value = {
    email: [],
    password: [],
  }
}

function normalizeErrors(value) {
  if (!value) return []
  return Array.isArray(value) ? value : [String(value)]
}

function goToLogin() {
  showSuccessDialog.value = false
  router.push({ name: 'Login' })
}

async function register() {
  if (saving.value) return

  const result = await formRef.value?.validate?.()

  if (!result?.valid) {
    toast.warning('Bitte überprüfen Sie Ihre Eingaben.')
    return
  }

  resetFieldErrors()
  saving.value = true

  const payload = {
    email: email.value,
    password: password.value,
    first_name: firstName.value,
    last_name: lastName.value,
  }

  try {
    await api.post('/auth/register/', payload)

    toast.success('Registrierung erfolgreich.')
    showSuccessDialog.value = true
  } catch (error) {
    const status = error?.response?.status
    const data = error?.response?.data || {}

    if (status === 400) {
      fieldErrors.value.email = normalizeErrors(
        data.email
      )

      fieldErrors.value.password = normalizeErrors(
        data.password
      )

      const nonFieldErrors = normalizeErrors(
        data.non_field_errors
      )

      nonFieldErrors.forEach(message => {
        toast.error(message)
      })

      if (
        !data.email &&
        !data.password &&
        !data.non_field_errors
      ) {
        toast.error('Bitte überprüfen Sie Ihre Eingaben.')
      }

      await formRef.value?.validate?.()
    } else {
      toast.error(
        'Bei der Registrierung ist ein Fehler aufgetreten.'
      )
    }
  } finally {
    saving.value = false
  }
}
</script>

<style scoped lang="scss">
.auth-page {
  min-height: 100%;
}

.auth-card {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 28px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

/* Header */
.auth-header {
  padding: 1.25rem 1.5rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.auth-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.auth-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Main content */
.auth-card-content {
  display: grid;
  gap: 1.25rem;
  padding: 1.5rem;
}

.registration-hint {
  border-radius: 20px;
}

/* Form */
.auth-form {
  display: grid;
  gap: 1.25rem;
}

.form-section {
  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.form-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

.auth-form :deep(.v-field) {
  border-radius: 18px;
}

.login-options {
  display: flex;
  justify-content: flex-end;
}

/* Footer */
.auth-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  padding: 1rem 1.5rem;
}

.auth-footer .v-btn {
  font-weight: 600;
  text-transform: none;
}

.submit-button {
  min-width: 150px;
}

/* Dialogs */
.auth-dialog {
  overflow: hidden;
  border-radius: 26px;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 0.85rem;

  padding: 1.25rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.dialog-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

.success-icon {
  background-color: rgba(var(--v-theme-success), 0.1);
}

.dialog-content {
  padding: 1.25rem;
  line-height: 1.55;
}

.dialog-actions {
  padding: 1rem 1.25rem;
}

.dialog-actions .v-btn {
  font-weight: 600;
  text-transform: none;
}

.dialog-alert {
  border-radius: 18px;
}

.email-summary {
  display: flex;
  align-items: center;
  gap: 0.65rem;

  padding: 0.8rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  background-color: rgba(var(--v-theme-primary), 0.04);

  font-weight: 600;
  word-break: break-word;
}

.support-link {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  text-decoration: none;
}

.support-link:hover {
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 700px) {
  .auth-page {
    padding-inline: 0.5rem;
  }

  .auth-header {
    padding: 1rem;
  }

  .auth-title-block {
    align-items: flex-start;
  }

  .auth-title-icon {
    width: 42px;
    height: 42px;
  }

  .auth-card-content {
    padding: 1rem;
  }

  .form-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .auth-footer {
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 1rem;
  }

  .auth-footer .v-btn {
    width: 100%;
  }

  .submit-button {
    min-width: 0;
  }

  .login-options {
    justify-content: center;
  }

  .dialog-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .dialog-content {
    padding: 1rem;
  }

  .dialog-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .dialog-actions .v-btn {
    width: 100%;
  }
}
</style>